import { useEffect, useCallback, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useCardStore, CARD_TYPES } from '@/stores/useCardStore'
import { fetchCards, fetchDomains, fetchStats } from '@/lib/api'
import { TypeBadge, DomainBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { cn } from '@/lib/utils'
import { Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'

export function CardLibrary() {
  const {
    cards,
    totalCards,
    hasMore,
    domains,
    stats,
    filters,
    setFilter,
    resetFilters,
    isLoading,
    setCards,
    setDomains,
    setStats,
    setLoading,
    setError,
  } = useCardStore()

  // Local search input state for controlled input
  const [searchInput, setSearchInput] = useState(filters.search)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Load initial data
  useEffect(() => {
    fetchDomains()
      .then(setDomains)
      .catch((e) => console.error('Failed to load domains:', e))

    fetchStats()
      .then(setStats)
      .catch((e) => console.error('Failed to load stats:', e))
  }, [setDomains, setStats])

  // Load cards when filters change
  useEffect(() => {
    setLoading(true)
    fetchCards({
      domain: filters.domain || undefined,
      phase: filters.phase || undefined,
      type: filters.type || undefined,
      search: filters.search || undefined,
      page: filters.page,
      page_size: filters.pageSize,
      sort_by: filters.sortBy,
      sort_order: filters.sortOrder,
    })
      .then((result) => {
        setCards(result.cards, result.total, result.has_more)
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Failed to load cards')
      })
      .finally(() => setLoading(false))
  }, [filters, setCards, setLoading, setError])

  // Debounced search - triggers on input change
  const handleSearchInput = useCallback(
    (value: string) => {
      setSearchInput(value)

      // Clear previous timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      // Debounce the filter update (300ms)
      debounceRef.current = setTimeout(() => {
        setFilter('search', value || '')
        setFilter('page', 1) // Reset to first page on new search
      }, 300)
    },
    [setFilter]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return (
    <div className="h-full flex flex-col">
      {/* Header - centered */}
      <header className="border-b border-border bg-card relative z-50">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-light">Card Library</h1>
            <p className="text-sm text-muted-foreground">
              {totalCards} cards in library
            </p>
          </div>
          <Button variant="outline" onClick={resetFilters}>
            Clear Filters
          </Button>
        </div>

        {/* Domain tabs */}
        {stats && (
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setFilter('domain', null)}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                filters.domain === null
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              )}
            >
              All ({stats.total_cards})
            </button>
            {domains.map((d) => (
              <button
                key={d.domain_id}
                onClick={() => setFilter('domain', d.name)}
                className={cn(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  filters.domain === d.name
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                )}
              >
                {d.name} ({stats.cards_by_domain[d.name] || 0})
              </button>
            ))}
          </div>
        )}

        {/* Filters row */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search title, summary, tags..."
                value={searchInput}
                onChange={(e) => handleSearchInput(e.target.value)}
                className="w-full h-10 pl-10 pr-4 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          <Select
            value={filters.type || 'all'}
            onValueChange={(v) => setFilter('type', v === 'all' ? null : v)}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {CARD_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        </div>
      </header>

      {/* Card grid - centered with generous spacing */}
      <div className="flex-1 overflow-auto relative z-0">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8 md:py-12">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : cards.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            No cards found
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 lg:gap-12">
            {cards.map((card) => (
              <Link
                key={card.card_id}
                to={`/cards/${card.card_id}`}
                className="block p-4 rounded-lg border border-border bg-card hover:border-foreground/30 transition-colors"
              >
                {card.title && (
                  <h3 className="font-medium mb-2 line-clamp-1">{card.title}</h3>
                )}

                <div className="flex items-center gap-2 mb-2">
                  <TypeBadge type={card.type} />
                  <DomainBadge domain={card.domain} />
                </div>

                <p className="text-sm line-clamp-3 mb-3">{card.summary}</p>

                {card.tags && card.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {card.tags.slice(0, 4).map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-muted-foreground"
                      >
                        #{tag}
                      </span>
                    ))}
                    {card.tags.length > 4 && (
                      <span className="text-xs text-muted-foreground">
                        +{card.tags.length - 4}
                      </span>
                    )}
                  </div>
                )}

                <div className="text-xs text-muted-foreground">
                  {card.edge_count} {card.edge_count === 1 ? 'link' : 'links'}
                </div>
              </Link>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Pagination - centered */}
      {totalCards > 0 && (
        <footer className="border-t border-border bg-card">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(filters.page - 1) * filters.pageSize + 1}-
            {Math.min(filters.page * filters.pageSize, totalCards)} of{' '}
            {totalCards}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilter('page', filters.page - 1)}
              disabled={filters.page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFilter('page', filters.page + 1)}
              disabled={!hasMore}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          </div>
        </footer>
      )}
    </div>
  )
}
